import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Batch 23 · Stages 1 + 2 — a text box that shows ALL its text: it starts one line high and grows with its content (no inner
 * scrollbar, no clipping). It replaces single-line inputs that hold sentences (form table cells, checklist comments, text
 * fields), so a long answer is readable without clicking in and scrolling.
 *
 * Behaviour kept from the single-line input it replaces (owner rule: only the control changes, the saved value is
 * byte-identical):
 *   * a plain Enter adds NO line break (an <input> could not hold one); the text wraps on screen only;
 *   * Tab moves on as before; onChange receives the same event shape (e.target.value);
 *   * value / placeholder / data-testid / disabled / readOnly pass straight through.
 * Shift+Enter is not special-cased either: no stored value can gain a newline the old control could not hold.
 */
const AutoGrowTextarea = React.forwardRef(({ className, value, onKeyDown, onChange, rows = 1, minRows = 1, ...props }, ref) => {
  const inner = React.useRef(null);
  const setRefs = (el) => {
    inner.current = el;
    if (typeof ref === "function") ref(el); else if (ref) ref.current = el;
  };
  const fit = React.useCallback(() => {
    const el = inner.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;     // + the 1 px border top and bottom
  }, []);
  React.useLayoutEffect(() => { fit(); }, [value, fit]);
  React.useEffect(() => {
    // the width changes when the window, the column or the font changes: grow / shrink again
    const el = inner.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let w = el.clientWidth;
    const ro = new ResizeObserver(() => { if (el.clientWidth !== w) { w = el.clientWidth; fit(); } });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);
  return (
    <textarea
      ref={setRefs}
      rows={Math.max(rows, minRows)}
      value={value}
      onChange={(e) => {
        // a pasted value with line breaks: the old single-line <input> STRIPPED them (HTML value sanitisation for text inputs
        // removes every CR and LF) — the same here, so the stored string is the one the old control would have stored
        if (/[\r\n]/.test(e.target.value)) e.target.value = e.target.value.replace(/[\r\n]/g, "");
        onChange?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.nativeEvent?.isComposing) e.preventDefault();
        onKeyDown?.(e);
      }}
      data-autogrow="true"
      className={cn(
        "block w-full resize-none overflow-hidden whitespace-pre-wrap break-words rounded-md border border-input bg-transparent px-2 py-1 text-sm leading-5 shadow-sm",
        "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
});
AutoGrowTextarea.displayName = "AutoGrowTextarea";

export { AutoGrowTextarea };
